import React, { useState, useMemo, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Square,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { CardHeader, CardContent, CardTitle } from "../ui/card";
import PipelineStatusBadge from "./PipelineStatusBadge";
import PipelineDetails from "./PipelineDetails";

// Custom CSS styles for status badges
const customStyles = `
  .status-badge-compact {
    padding-right: 0.5rem !important;
  }
`;

/**
 * Utility function for formatting durations
 */
const formatDuration = (ms) => {
  if (!ms) return "-";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
};

/**
 * Extract the deployment target from a pipeline selector pattern
 * For example, "Deploy to BU Global Program" -> "BU Global Program"
 */
const extractDeploymentTarget = (pattern) => {
  if (!pattern) return "Default";

  // For "Deploy to X" patterns, extract X
  if (pattern.toLowerCase().startsWith("deploy to ")) {
    return pattern.substring("Deploy to ".length);
  }

  return pattern || "Default";
};

/**
 * Component for displaying pipeline runs in a table grouped by deployment target
 */
const GroupedPipelineRunsTable = ({
  viewAllRepos,
  title,
  pipelineRuns,
  isLoadingRuns,
  searchTerm,
  setSearchTerm,
  handleRefreshPipelines,
  expandedRunIds,
  toggleExpandRun,
  handleStopPipeline,
  setViewAllRepos,
  setSelectedRepository,
  formatDateTime,
  workspace,
}) => {
  // State for tracking which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState({});

  // Initialize all groups as expanded when component mounts or when new pipeline runs are added
  useEffect(() => {
    if (pipelineRuns.length > 0) {
      setExpandedGroups((currentGroups) => {
        const initialExpandedState = { ...currentGroups };

        // Create entries for all unique deployment targets
        pipelineRuns.forEach((run) => {
          const pattern = run.target?.selector?.pattern || "Default";
          const target = extractDeploymentTarget(pattern);
          // Only set if not already set (preserve user preferences)
          if (initialExpandedState[target] === undefined) {
            initialExpandedState[target] = true;
          }
        });

        return initialExpandedState;
      });
    }
  }, [pipelineRuns]);

  // Group pipeline runs by deployment target
  const groupedPipelines = useMemo(() => {
    if (!pipelineRuns.length) return {};

    return pipelineRuns.reduce((groups, run) => {
      const pattern = run.target?.selector?.pattern || "Default";
      const target = extractDeploymentTarget(pattern);

      if (!groups[target]) {
        groups[target] = [];
      }

      groups[target].push(run);
      return groups;
    }, {});
  }, [pipelineRuns]);

  // Toggle a group's expanded state
  const toggleGroup = (target) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [target]: !prev[target],
    }));
  };

  // Expand all groups
  const expandAllGroups = () => {
    const expanded = {};
    Object.keys(groupedPipelines).forEach((target) => {
      expanded[target] = true;
    });
    setExpandedGroups(expanded);
  };

  // Collapse all groups
  const collapseAllGroups = () => {
    setExpandedGroups({});
  };

  return (
    <>
      {/* Add custom CSS styles */}
      <style>{customStyles}</style>

      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <CardTitle>{title}</CardTitle>
          <div className="flex mt-2 sm:mt-0 gap-2">
            <Input
              placeholder="Search branches or builds"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64 h-9"
            />
            <Button
              onClick={handleRefreshPipelines}
              className="flex items-center"
              variant="outline"
              size="sm"
              disabled={isLoadingRuns}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoadingRuns ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoadingRuns && pipelineRuns.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : pipelineRuns.length > 0 ? (
          <div>
            <div className="flex justify-end mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={expandAllGroups}
                className="text-xs mr-2 h-8"
              >
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={collapseAllGroups}
                className="text-xs h-8"
              >
                Collapse All
              </Button>
            </div>

            {Object.entries(groupedPipelines).map(([target, runs]) => {
              const isExpanded = expandedGroups[target] || false;

              return (
                <div
                  key={target}
                  className="mb-4 border border-border rounded-md overflow-hidden"
                >
                  <div
                    className="flex justify-between items-center p-2 bg-muted/70 cursor-pointer"
                    onClick={() => toggleGroup(target)}
                  >
                    <div className="flex items-center">
                      <span className="mr-2 text-xs text-muted-foreground w-4">
                        {isExpanded ? "▼" : "►"}
                      </span>
                      <span className="font-medium">{target}</span>
                    </div>
                    <div className="text-sm text-muted-foreground pr-3">
                      {runs.length} pipeline{runs.length !== 1 ? "s" : ""}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="overflow-x-auto">
                      <Table className="w-full">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[50px]"></TableHead>
                            <TableHead className="w-[80px]">Build</TableHead>
                            {viewAllRepos && (
                              <TableHead className="w-[250px]">
                                Repository
                              </TableHead>
                            )}
                            <TableHead className="w-[250px]">Branch</TableHead>
                            <TableHead className="w-[120px]">Status</TableHead>
                            <TableHead className="w-[200px]">Started</TableHead>
                            <TableHead className="w-[80px]">Duration</TableHead>
                            <TableHead className="w-[180px] text-right">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {runs.map((run) => {
                            const isExpanded = expandedRunIds.includes(
                              run.uuid
                            );
                            const duration =
                              run.completed_on && run.created_on
                                ? new Date(run.completed_on) -
                                  new Date(run.created_on)
                                : null;

                            return (
                              <React.Fragment
                                key={`${run.repository.name}-${run.repository.slug}-${run.build_number}-${run.uuid}`}
                              >
                                <TableRow
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => toggleExpandRun(run.uuid)}
                                >
                                  <TableCell>
                                    {isExpanded ? (
                                      <ChevronUp className="w-4 h-4" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4" />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    #{run.build_number}
                                  </TableCell>
                                  {viewAllRepos && (
                                    <TableCell>
                                      <Button
                                        variant="link"
                                        className="p-0 h-auto text-left font-normal"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setViewAllRepos(false);
                                          setSelectedRepository(
                                            run.repository.slug
                                          );
                                        }}
                                      >
                                        {run.repository.name}
                                      </Button>
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    {run.target?.ref_name || "-"}
                                  </TableCell>
                                  <TableCell>
                                    <div className="status-badge-compact">
                                      <PipelineStatusBadge
                                        status={run.state?.name}
                                        stage={run.state?.stage?.name}
                                      />
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {formatDateTime(run.created_on)}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {formatDuration(duration)}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      size="sm"
                                      variant="default"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                          `https://bitbucket.org/${workspace}/${run.repository.slug}/pipelines/results/${run.uuid}`,
                                          "_blank"
                                        );
                                      }}
                                    >
                                      View in Bitbucket
                                    </Button>
                                    {run.state?.name === "IN_PROGRESS" &&
                                      run.state?.stage?.name !== "PAUSED" && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleStopPipeline(
                                              run.uuid,
                                              run.repository.slug
                                            );
                                          }}
                                          className="ml-2"
                                        >
                                          <Square className="w-4 h-4 mr-1" />
                                          Stop
                                        </Button>
                                      )}
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={viewAllRepos ? 8 : 7}
                                      className="bg-muted/20 p-4"
                                    >
                                      <PipelineDetails
                                        run={run}
                                        workspace={workspace}
                                        formatDateTime={formatDateTime}
                                      />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Loading indicator during progressive loading */}
            {isLoadingRuns && pipelineRuns.length > 0 && (
              <div className="text-center py-4 mt-2">
                <div className="flex justify-center items-center">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                  <span className="text-sm text-muted-foreground">
                    Loading more pipelines...
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {viewAllRepos
                ? "No running or failed pipelines found"
                : "No pipeline runs found"}
            </p>
          </div>
        )}
      </CardContent>
    </>
  );
};

export default GroupedPipelineRunsTable;
